// ClientSupportTickets.tsx
import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useWebSocket } from "../hooks/useWebSocket";
import {
    MessageCircle,
    Search,
    Filter,
    X,
    Plus,
    ChevronDown,
    AlertCircle,
    CheckCircle,
    Clock,
    FileText,
    Paperclip,
    Send,
    RefreshCw,
    Flag,
    Users,
    Calendar,
    MoreVertical,
    Download,
    ExternalLink,
    Mail,
    Phone,
    User,
    Home,
    Wallet,
    Shield,
    Bell,
    ChartAreaIcon,
    Menu,
} from "lucide-react";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import SharedHeader5 from "../Headers/SharedHeader5";
import SharedFooter3 from "../Footer/SharedFooter3";
import { useSelector } from "react-redux";
import {
    createChat,
    createConversation,
    createUserTickets,
    EmptyConversations,
    getUserChats,
    getUserConversations,
    getUserTickets,
    UpdateUserChats,
    useAppDispatch,
} from "../redux";
import NewTicketModal from "./Modals/NewTicketModal";
import ReportModal from "./Modals/ReportModal";
import Spinner from "../modules/shared/components/Spinner";
import { hideLoader, selectLoaderValue, showLoader } from "../redux/Slices/loadingSlice";
import { CategoryEnum, ContextEnum, PriorityEnum, RoleEnum, TicketStatusesEnum } from "../modules/shared/model";
import TicketDetailsModal from "./Modals/TicketDetailsModal";
import ProfileCompletionDialog from "../components/ui/ProfileCompletionDialog";
import { useNavigate } from "react-router-dom";
import { fetchCommonQuestionsByCountry, clearCountryFaqs } from "../redux/Slices/CommonQuestionsSlice";
import { getAllCountries } from "../redux/Actions/AdminAction";
import { getCachedCountryPreference } from "../lib/currencyUtils";
import { CommonQuestionCategoryEnum } from "../modules/shared/model/common-questions.enum";
import { Loader2, HelpCircle, Minus } from "lucide-react";

const ClientSupportTickets = () => {
    // States
    const { t } = useTranslation();
    const isLoading = useSelector(selectLoaderValue);
    const [showNewTicketModal, setShowNewTicketModal] = useState(false);
    const navigate = useNavigate();
    const [showTicketDetails, setShowTicketDetails] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [attachments, setAttachments] = useState<any>([]);
    const [showProfileCompletionDialog, setShowProfileCompletionDialog] = useState(false);

    const [searchQuery, setSearchQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [priorityFilter, setPriorityFilter] = useState("all");
    const [newMessage, setNewMessage] = useState("");
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [newReportModal, setNewReportModal] = useState(false);
    const { user, userTickets, chats, conversations, token } = useSelector((state: any) => state.auth);
    const { countryFaqs, countryFaqsLoading, countryFaqsError } = useSelector((state: any) => state.commonQuestions);
    const dispatch = useAppDispatch();
    
    // FAQ states
    const [expandedFaq, setExpandedFaq] = useState<string | null>(null);
    const [isFaqSectionExpanded, setIsFaqSectionExpanded] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState<string>("all");
    const [faqCountryId, setFaqCountryId] = useState<string | null>(null);

    const mapApiTicketsToLocal = (apiTickets: any[]) => {
        return apiTickets.map(ticket => ({
            id: ticket.id,
            subject: ticket.subject,
            description: ticket.description,
            status: ticket.status.id,
            priority: ticket.priority.id,
            category: ticket.category.id,
            createdAt: ticket.createdAt,
            updatedAt: ticket.updatedAt,
            messages: [
                // {
                //     id: 1,
                //     sender: `${user?.firstName} ${user?.lastName}`,
                //     role: "client",
                //     message: ticket.description,
                //     timestamp: ticket.createdAt,
                //     attachments: [],
                // },
                // {
                //     id: 2,
                //     sender: "Support Team",
                //     role: "support",
                //     message:
                //         "Thank you for reporting this issue. Could you please provide the transaction ID and any error messages you&apos;re seeing?",
                //     timestamp: "2025-06-20T10:45:00",
                //     attachments: [],
                // },
            ],
            conversations: conversations,

            assignee: "Support Team",
            department: getCategoryName(Number(ticket.category.id)),
        }));
    };
    const getCurrentChatData = async (ticket: any) => {
        const { payload } = await dispatch(getUserChats({ id: ticket?.id }));
        if (payload?.id) {
            await dispatch(getUserConversations({ id: payload?.id }));
        }
        setShowTicketDetails(true);
    };
    const getCategoryName = (categoryId: number) => {
        switch (Number(categoryId)) {
            case CategoryEnum.Technical:
                return "Technical Support";
            case CategoryEnum.Billing:
                return "Billing Support";
            case CategoryEnum.General:
                return "General Support";
            case CategoryEnum.Payment:
                return "Payment Support";
            default:
                return "General Support";
        }
    };
    const getRoleName = (roleId: number) => {
        return RoleEnum[roleId] || "Support_Ticket";
    };
    const getStatusName = (statusId: number) => {
        return TicketStatusesEnum[statusId] || "Open";
    };
    const getPriorityName = (priorityId: number) => {
        return PriorityEnum[priorityId] || "Medium";
    };

    const [tickets, setTickets] = useState<any[]>([]);

    useEffect(() => {
        if (userTickets && userTickets.length > 0) {
            setTickets(mapApiTicketsToLocal(userTickets));
        } else {
            setTickets([]);
        }
    }, [userTickets]);

    const getAllCurrentUserTickets = useCallback(async () => {
        await dispatch(getUserTickets({ id: user?.id }));
    }, [dispatch, user?.id]);

    useEffect(() => {
        getAllCurrentUserTickets();
    }, [getAllCurrentUserTickets]);

    // Real-time WebSocket listener for support ticket messages
    const handleWebSocketMessage = useCallback((message: any) => {
        // Handle new support messages
        if (message.type === "new_support_message" || message.type === "support_ticket_update" || message.type === "new_conversation") {
            const { ticketId, chatId, senderId } = message.detail || {};
            
            // Check if the message is from admin/support (not from the current user)
            const isFromAdmin = senderId && senderId !== user?.id;
            
            // If we have a selected ticket and it matches the incoming message
            if (selectedTicket && selectedTicket.id === ticketId) {
                // Refresh conversations for the current ticket immediately
                if (chatId || chats?.id) {
                    dispatch(getUserConversations({ id: chatId || chats?.id })).catch((err) => {
                        console.error("Failed to refresh conversations:", err);
                    });
                }
            } else if (isFromAdmin) {
                // Message from admin for a different ticket - refresh tickets list
                getAllCurrentUserTickets();
            }
            
            // Refresh the tickets list to update any status/priority changes
            if (message.type === "support_ticket_update") {
                getAllCurrentUserTickets();
            }
        }
    }, [user?.id, selectedTicket?.id, chats?.id, showTicketDetails, dispatch, getAllCurrentUserTickets]);

    const { isConnected: isWebSocketConnected } = useWebSocket({
        onMessage: handleWebSocketMessage
    });

    // Fetch FAQs based on cached country or USA fallback
    useEffect(() => {
        const fetchFaqCountryAndData = async () => {
            try {
                // First check for cached country preference
                const cachedCountryId = getCachedCountryPreference();
                
                if (cachedCountryId) {
                    console.log("✅ [FAQ] Using cached country ID:", cachedCountryId);
                    setFaqCountryId(cachedCountryId);
                    // Fetch FAQs for cached country
                    await dispatch(fetchCommonQuestionsByCountry({
                        countryId: cachedCountryId,
                        filters: {
                            page: 1,
                            limit: 50,
                            sortBy: "createdAt",
                            sortOrder: "desc"
                        }
                    }));
                } else {
                    // No cache found, fallback to United States
                    console.log("⚠️ [FAQ] No cached country found, searching for United States...");
                    const result = await dispatch(getAllCountries({
                        page: 1,
                        limit: 5,
                        search: "United States",
                        isShowLoader: false,
                        append: false
                    }));
                    
                    if (getAllCountries.fulfilled.match(result)) {
                        const usCountry = result.payload?.data?.find((country: any) => 
                            country.name?.toLowerCase().includes("united states") ||
                            country.name?.toLowerCase() === "united states" ||
                            country.initial?.toLowerCase() === "us"
                        );
                        
                        if (usCountry?.id) {
                            console.log("✅ [FAQ] Found United States country, ID:", usCountry.id);
                            setFaqCountryId(usCountry.id);
                            // Fetch FAQs for USA
                            await dispatch(fetchCommonQuestionsByCountry({
                                countryId: usCountry.id,
                                filters: {
                                    page: 1,
                                    limit: 50,
                                    sortBy: "createdAt",
                                    sortOrder: "desc"
                                }
                            }));
                        }
                    }
                }
            } catch (error) {
                console.error("❌ [FAQ] Error fetching FAQ country:", error);
            }
        };

        fetchFaqCountryAndData();
    }, [dispatch]);

    // Check if user needs to complete profile
    const checkProfileCompletion = () => {
        if (user && !user.phone) {
            setShowProfileCompletionDialog(true);
            return false; // Profile incomplete
        }
        return true; // Profile complete
    };

    // Helper function to navigate to profile based on user role
    const navigateToProfile = () => {
        if (!user?.role?.id) return;
        
        const roleId = Number(user.role.id);
        if (roleId === RoleEnum.professional || roleId === RoleEnum.internationl_professional) {
            navigate("/myprofile");
        } else if (roleId === RoleEnum.client || roleId === RoleEnum.internationl_client) {
            navigate("/client/profile");
        } else if (roleId === RoleEnum.admin) {
            navigate("/admin/dashboard");
        }
        setShowProfileCompletionDialog(false);
    };

    const [newTicket, setNewTicket] = useState<any>({
        subject: "",
        description: "",
        priority: "",
        category: "",
        status: TicketStatusesEnum.Open,
        createdAt: new Date().toISOString(),
    });
    const onSendMessage = async () => {
        if (newMessage.trim() || attachments.length > 0) {
            try {
                dispatch(showLoader());
                let chatResponse;
                // First create the chat
                const chatPayload = {
                    unreadCount: {},
                    participants: [{ id: user?.id }],
                    contextId: selectedTicket.id,
                    contextType: { id: ContextEnum.Support_Ticket },
                    lastMessageAt: new Date().toISOString(),
                    lastMessage: newMessage || `Sent ${attachments.length} attachment(s)`,
                };
                if (chats != null) {
                    chatResponse = await dispatch(UpdateUserChats({ id: chats?.id, data: chatPayload })).unwrap();
                } else {
                    chatResponse = await dispatch(createChat({ data: chatPayload })).unwrap();
                }

                // Then create the conversation with attachments
                const conversationPayload = {
                    isRead: false,
                    message: newMessage,
                    sender: { id: user?.id },
                    chatId: { id: chatResponse.id },
                    attachments: attachments.map(att => ({ id: att.id })),
                };

                await dispatch(createConversation({ data: conversationPayload }));
                
                // Immediately refresh conversations to show the new message
                await dispatch(getUserConversations({ id: chatResponse.id }));
                
                // WebSocket message sending is handled in TicketDetailsModal
                // No need to send here as it's already sent from the modal

                // Clear input fields
                setNewMessage("");
                setAttachments([]);
                    } catch (error) {
        } finally {
                dispatch(hideLoader());
            }
        }
    };
    const handleSubmitNewTicket = async (isReport = false) => {
        // Check if user needs to complete profile
        if (!checkProfileCompletion()) {
            return;
        }

        try {
            const ticketData = {
                subject: newTicket.subject,
                description: newTicket.description,
                priority: { id: newTicket.priority },
                category: { id: newTicket.category },
                status: { id: TicketStatusesEnum.Open },
                userId: { id: user?.id },
            };
            await dispatch(createUserTickets({ data: ticketData }));
            setNewTicket({
                subject: "",
                description: "",
                priority: "",
                category: "",
                status: TicketStatusesEnum.Open,
                createdAt: new Date().toISOString(),
            });

            setShowNewTicketModal(false);
            setNewReportModal(false);
                } catch (error) {
        }
    };

    const getStatusColor = (statusId: number) => {
        switch (statusId) {
            case TicketStatusesEnum.Open:
                return "bg-green-100 text-green-800";
            case TicketStatusesEnum.In_Progress:
                return "bg-blue-100 text-blue-800";
            case TicketStatusesEnum.Resolved:
                return "bg-gray-100 text-gray-800";
            default:
                return "bg-gray-100 text-gray-800";
        }
    };

    const getPriorityColor = (priorityId: number) => {
        switch (priorityId) {
            case PriorityEnum.High:
                return "bg-red-100 text-red-800";
            case PriorityEnum.Medium:
                return "bg-yellow-100 text-yellow-800";
            case PriorityEnum.Low:
                return "bg-green-100 text-green-800";
            default:
                return "bg-gray-100 text-gray-800";
        }
    };

    const filteredTickets = tickets
        .filter(ticket => {
            const matchesSearch =
                ticket.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
                ticket.description.toLowerCase().includes(searchQuery.toLowerCase());

            const matchesStatus =
                statusFilter === "all" || getStatusName(ticket.status).toLowerCase() === statusFilter.toLowerCase();

            const matchesPriority =
                priorityFilter === "all" || getPriorityName(ticket.priority).toLowerCase() === priorityFilter.toLowerCase();

            return matchesSearch && matchesStatus && matchesPriority;
        })
        .sort((a, b) => {
            // Sort by createdAt descending (newest first)
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        });

    const formatDate = dateString => {
        const date = new Date(dateString);
        return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    // FAQ helper functions
    const toggleFaq = (faqId: string) => {
        setExpandedFaq(expandedFaq === faqId ? null : faqId);
    };

    const handleCategoryChange = (category: string) => {
        setSelectedCategory(category);
    };

    const getFilteredFaqs = () => {
        if (!countryFaqs || countryFaqs.length === 0) return [];
        
        if (selectedCategory === "all") {
            return countryFaqs;
        }
        
        const categoryId = parseInt(selectedCategory);
        return countryFaqs.filter(faq => Number(faq.category?.id) === categoryId);
    };

    const getAvailableCategories = () => {
        if (!countryFaqs || countryFaqs.length === 0) return [];
        
        const categories = new Set<number>();
        countryFaqs.forEach(faq => {
            if (faq.category?.id) {
                categories.add(Number(faq.category.id));
            }
        });
        
        return Array.from(categories).map(categoryId => {
            const categoryName = Object.keys(CommonQuestionCategoryEnum).find(
                key => CommonQuestionCategoryEnum[key as keyof typeof CommonQuestionCategoryEnum] === categoryId
            );
            return { id: categoryId, name: categoryName || `Category ${categoryId}` };
        });
    };

    return (
        <>
            {isLoading && (
                <div className="absolute top-[50%] left-[50%]">
                    <Spinner color="gray" />
                </div>
            )}
            <div
                className={`min-h-screen bg-gray-50 ${isLoading ? "opacity-25" : "opacity-100"}`}
                style={{ textAlign: "left" }}>
                {/* Fixed Header */}
                <div className="fixed top-0 inset-x-0 z-50 bg-white border-b h-16">
                    <SharedHeader5 />
                </div>

                <div className="flex min-h-[calc(100vh-64px)] pt-16">

                    {/* Main Content */}
                    <main className="flex-1 px-6 py-8 max-w-6xl mx-auto">
                        {/* Page Header */}
                        <div className="mb-8">
                            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                                <div>
                                    <h1 className="text-2xl font-bold text-gray-900">{t("supportTickets.title")}</h1>
                                    <p className="text-gray-600">{t("supportTickets.subtitle")}</p>
                                </div>
                                <Button onClick={() => setShowNewTicketModal(true)} className="w-full sm:w-auto">
                                    <Plus className="w-4 h-4 mr-2" />
                                    {t("supportTickets.newTicket")}
                                </Button>
                            </div>
                        </div>

                        {/* Reports Section (Highlighted) */}
                        {/* <Card className="p-6 mb-6 bg-blue-50 border-blue-200">
                            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                                <div>
                                    <h2 className="text-xl font-semibold text-blue-900">{t("supportTickets.reports")}</h2>
                                    <p className="text-blue-700">
                                        {t("supportTickets.reportsDescription")}
                                    </p>
                                </div>
                                <Button
                                    onClick={() => setNewReportModal(true)}
                                    className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700">
                                    <Flag className="w-4 h-4 mr-2" />
                                    {t("supportTickets.fileAReport")}
                                </Button>
                            </div>
                        </Card> */}

                        {/* Filters and Search */}
                        <Card className="p-6 mb-6">
                            <div className="flex flex-col sm:flex-row gap-4">
                                <div className="flex-1 relative">
                                    <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder={t("supportTickets.searchTicketsAndReports")}
                                        value={searchQuery}
                                        onChange={e => setSearchQuery(e.target.value)}
                                        className="w-full pl-10 pr-4 py-2 border rounded-lg"
                                    />
                                </div>
                                <div className="flex gap-2">
                                    {/* Status Filter Dropdown */}
                                    <select
                                        value={statusFilter}
                                        onChange={e => setStatusFilter(e.target.value)}
                                        className="px-3 py-2 border rounded-lg">
                                        <option value="all">{t("supportTickets.allStatus")}</option>
                                        {Object.keys(TicketStatusesEnum)
                                            .filter(key => isNaN(Number(key)))
                                            .map(key => (
                                                <option key={key} value={key.toLowerCase().replace("_", " ")}>
                                                    {key.replace("_", " ")}
                                                </option>
                                            ))}
                                    </select>

                                    {/* Priority Filter Dropdown */}
                                    <select
                                        value={priorityFilter}
                                        onChange={e => setPriorityFilter(e.target.value)}
                                        className="px-3 py-2 border rounded-lg">
                                        <option value="all">{t("supportTickets.allPriority")}</option>
                                        {Object.keys(PriorityEnum)
                                            .filter(key => isNaN(Number(key)))
                                            .map(key => (
                                                <option key={key} value={key.toLowerCase()}>
                                                    {key}
                                                </option>
                                            ))}
                                    </select>
                                </div>
                            </div>
                        </Card>

                        {/* FAQ Section */}
                        <div className="mb-6">
                            <Card className="border border-gray-200 rounded-lg">
                                <button
                                    onClick={() => setIsFaqSectionExpanded(!isFaqSectionExpanded)}
                                    className="w-full px-4 py-3 sm:px-6 sm:py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                                >
                                    <h2 className="text-xl sm:text-2xl font-semibold flex items-center gap-2">
                                        <HelpCircle className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
                                        {t("supportTickets.frequentlyAskedQuestions")}
                                    </h2>
                                    {isFaqSectionExpanded ? (
                                        <Minus className="h-5 w-5 sm:h-6 sm:w-6 text-gray-500 flex-shrink-0" />
                                    ) : (
                                        <Plus className="h-5 w-5 sm:h-6 sm:w-6 text-gray-500 flex-shrink-0" />
                                    )}
                                </button>
                                
                                {isFaqSectionExpanded && (
                                    <div className="px-4 py-3 sm:px-6 sm:py-4 border-t border-gray-200 bg-gray-50">
                                        {countryFaqsLoading ? (
                                            <div className="flex flex-col items-center justify-center py-8">
                                                <Loader2 className="h-8 w-8 animate-spin text-blue-600 mb-3" />
                                                <span className="text-base text-gray-700 font-medium mb-1">
                                                    {t("supportTickets.loadingFaqs")}
                                                </span>
                                                <span className="text-xs text-gray-500">
                                                    {t("supportTickets.pleaseWait")}
                                                </span>
                                            </div>
                                        ) : countryFaqsError ? (
                                            <div className="text-center py-8">
                                                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg max-w-md mx-auto">
                                                    {countryFaqsError}
                                                </div>
                                            </div>
                                        ) : countryFaqs && countryFaqs.length > 0 ? (
                                            <div className="space-y-4">
                                                {/* Category Filter Dropdown */}
                                                {getAvailableCategories().length > 0 && (
                                                    <div className="flex items-center gap-4">
                                                        <label className="text-sm font-medium text-gray-700">
                                                            {t("supportTickets.filterByCategory")}
                                                        </label>
                                                        <select
                                                            value={selectedCategory}
                                                            onChange={(e) => handleCategoryChange(e.target.value)}
                                                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                        >
                                                            <option value="all">All Categories</option>
                                                            {getAvailableCategories().map((category) => (
                                                                <option key={Number(category.id)} value={Number(category.id)}>
                                                                    {category.name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                )}

                                                {/* Filtered FAQs */}
                                                <div className="space-y-2">
                                                    {getFilteredFaqs().map((faq) => (
                                                        <div key={faq.id} className="border border-gray-200 rounded-lg bg-white">
                                                            <button
                                                                onClick={() => toggleFaq(faq.id)}
                                                                className="w-full px-4 py-3 sm:px-6 sm:py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                                                            >
                                                                <div className="flex-1 min-w-0">
                                                                    <h3 className="font-medium text-sm sm:text-base pr-4">
                                                                        {faq.question}
                                                                    </h3>
                                                                    {faq.category && (
                                                                        <p className="text-xs text-gray-500 mt-1">
                                                                            {faq.category.name}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                                {expandedFaq === faq.id ? (
                                                                    <Minus className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500 flex-shrink-0" />
                                                                ) : (
                                                                    <Plus className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500 flex-shrink-0" />
                                                                )}
                                                            </button>
                                                            {expandedFaq === faq.id && (
                                                                <div className="px-4 py-3 sm:px-6 sm:py-4 border-t border-gray-200 bg-gray-50">
                                                                    <p className="text-sm sm:text-base text-gray-700 whitespace-pre-line">
                                                                        {faq.answer}
                                                                    </p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}
                                                    
                                                                    {/* No FAQs message */}
                                                                    {getFilteredFaqs().length === 0 && (
                                                                        <div className="text-center py-8">
                                                                            <div className="bg-gray-50 border border-gray-200 text-gray-600 px-4 py-3 rounded-lg max-w-md mx-auto">
                                                                                {t("supportTickets.noFaqsForCategory")}
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div className="text-center py-8">
                                                                <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg max-w-md mx-auto">
                                                                    <p className="font-medium mb-2">{t("supportTickets.noFaqsAvailable")}</p>
                                                                    <p className="text-sm">
                                                                        {t("supportTickets.checkBackLater")}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        )}
                                    </div>
                                )}
                            </Card>
                        </div>

                        {/* Tickets List */}
                        <div className="space-y-4">
                            {filteredTickets.map(ticket => (
                                <Card
                                    key={ticket.id}
                                    className="p-4 cursor-pointer hover:shadow-md transition-shadow"
                                    onClick={async () => {
                                        setSelectedTicket(ticket);
                                        await getCurrentChatData(ticket);
                                    }}>
                                    <div className="flex flex-col sm:flex-row gap-4 justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-start justify-between">
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <h3 className="text-lg font-medium text-gray-900">
                                                            {ticket.subject}
                                                        </h3>
                                                        <Badge variant={undefined} className="text-xs">
                                                            {ticket.id}
                                                        </Badge>
                                                    </div>
                                                    <p className="text-sm text-gray-600 mt-1">{ticket.description}</p>
                                                </div>
                                            </div>
                                            <div className="flex flex-wrap items-center gap-3 mt-3">
                                                <Badge
                                                    variant={undefined}
                                                    className={getStatusColor(Number(ticket.status))}>
                                                    {getStatusName(ticket.status)}
                                                </Badge>
                                                <Badge
                                                    variant={undefined}
                                                    className={getPriorityColor(Number(ticket.priority))}>
                                                    {getPriorityName(ticket.priority)}
                                                </Badge>
                                                <span className="text-sm text-gray-500 flex items-center gap-1">
                                                    <Calendar className="w-4 h-4" />
                                                    {formatDate(ticket.createdAt)}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-4">
                                            <div className="text-right">
                                                <p className="text-sm font-medium text-gray-900">{ticket.assignee}</p>
                                                <p className="text-xs text-gray-500">{ticket.department}</p>
                                            </div>
                                            <ChevronDown className="w-5 h-5 text-gray-400" />
                                        </div>
                                    </div>
                                </Card>
                            ))}
                        </div>

                        {/* New Ticket Modal */}
                        <NewTicketModal
                            show={showNewTicketModal}
                            onClose={() => setShowNewTicketModal(false)}
                            onSubmit={handleSubmitNewTicket}
                            newTicket={newTicket}
                            setNewTicket={setNewTicket}
                        />

                        {/* New Report Modal */}
                        <ReportModal
                            show={newReportModal}
                            onClose={() => setNewReportModal(false)}
                            onSubmit={data => handleSubmitNewTicket(true)}
                            newTicket={newTicket}
                            setNewTicket={setNewTicket}
                        />

                        {/* Ticket Details Modal */}
                        <AnimatePresence>
                            {showTicketDetails && selectedTicket && (
                                <TicketDetailsModal
                                    ticket={selectedTicket}
                                    onClose={() => {
                                        setShowTicketDetails(false);
                                        dispatch(EmptyConversations());
                                    }}
                                    newMessage={newMessage}
                                    setNewMessage={setNewMessage}
                                    onSendMessage={onSendMessage}
                                    attachments={attachments}
                                    setAttachments={setAttachments}
                                    getStatusColor={getStatusColor}
                                    getPriorityColor={getPriorityColor}
                                    getRoleName={getRoleName}
                                    getStatusName={getStatusName}
                                    getPriorityName={getPriorityName}
                                    getCategoryName={getCategoryName}
                                    formatDate={formatDate}
                                    conversations={conversations}
                                    user={user}
                                />
                            )}
                        </AnimatePresence>

                        {/* Profile Completion Dialog */}
                        <ProfileCompletionDialog
                            isOpen={showProfileCompletionDialog}
                            onClose={() => setShowProfileCompletionDialog(false)}
                            onGoToProfile={navigateToProfile}
                            userRole={user?.role?.id}
                        />
                    </main>
                </div>

                <br />
                <br />
                <br />
                <br />

                <SharedFooter3 />
            </div>
        </>
    );
};

export default ClientSupportTickets;
